import classNames from 'classnames'
import Modal from 'react-modal'
import { motion } from 'framer-motion'
import { PiX } from 'react-icons/pi'
import type ReactModal from 'react-modal'
import type { ReactNode } from 'react'
import './redesigns.css'

export interface RdaDrawerProps extends ReactModal.Props {
  bodyClass?: string
  height?: string | number
  onClose?: () => void
  placement?: 'top' | 'right' | 'bottom' | 'left'
  title?: string | ReactNode
  width?: string | number
  /**
   * Caps the right/left panel at 560px instead of the default 480px on
   * screens 640px and up (full width is unaffected below that). For
   * component drawers that host roomier content, such as the AI chat or
   * the file browser.
   */
  wide?: boolean
}

type Placement = 'top' | 'right' | 'bottom' | 'left'

const OFFSCREEN: Record<Placement, { x?: string; y?: string }> = {
  right: { x: '100%' },
  left: { x: '-100%' },
  top: { y: '-100%' },
  bottom: { y: '100%' },
}

/**
 * The redesign's drawer frame: the same primitive as the classic `Drawer`
 * (`react-modal`, so the focus trap, Escape, overlay click and body scroll
 * lock are unchanged), restyled with the `--rda-*` tokens. A full-height side
 * panel on `right` (today's only vertical use), a rounded bottom sheet with a
 * grab handle on `bottom`.
 *
 * Drawers render through a portal outside `AssetShell`/`AssetDrawerScope`, so
 * the organisation's accent is not in scope here - the header, close target
 * and panel use only the fixed half of the palette (surface, ink, hairline),
 * which is why they need no `accent` prop. The body still gets the accent
 * through `AssetDrawerScope`, same as before.
 *
 * The classic `Drawer` slides by an offset equal to its own width/height,
 * which is undefined for a `height="auto"` sheet (see FirstTimeSheet /
 * NudgeSerialSheet). This one slides with a `transform` percentage instead,
 * so the open/close animation is correct regardless of the panel's measured
 * size.
 */
export default function RdaDrawer(props: RdaDrawerProps) {
  const {
    bodyClass,
    children,
    closeTimeoutMS = 300,
    height = 'auto',
    isOpen,
    onClose,
    placement = 'right',
    title,
    width = 400,
    wide = false,
    ...rest
  } = props

  const vertical = placement === 'left' || placement === 'right'
  const isSheet = placement === 'bottom'
  const offscreen = OFFSCREEN[placement]

  return (
    <Modal
      className={{
        base: classNames('rda-drawer', `rda-drawer--${placement}`),
        afterOpen: 'rda-drawer-after-open',
        beforeClose: 'rda-drawer-before-close',
      }}
      overlayClassName={{
        base: 'rda-drawer-overlay',
        afterOpen: 'rda-drawer-overlay-after-open',
        beforeClose: 'rda-drawer-overlay-before-close',
      }}
      portalClassName="rda-drawer-portal"
      bodyOpenClassName="rda-drawer-open rda-drawer-lock-scroll"
      ariaHideApp={false}
      isOpen={isOpen}
      closeTimeoutMS={closeTimeoutMS}
      {...rest}
    >
      <motion.div
        className={classNames(
          'rda-drawer-panel',
          vertical ? 'rda-drawer-panel--vertical' : 'rda-drawer-panel--horizontal',
          isSheet && 'rda-drawer-panel--sheet',
          vertical && wide && 'rda-drawer-panel--wide',
        )}
        style={
          vertical
            ? { width }
            : { height: height === 'auto' ? undefined : height }
        }
        initial={offscreen}
        animate={isOpen ? { x: 0, y: 0 } : offscreen}
        transition={{ duration: closeTimeoutMS / 1000, ease: [0.16, 1, 0.3, 1] }}
      >
        {isSheet ? <span aria-hidden className="rda-drawer-handle" /> : null}

        {title || onClose ? (
          <div className="rda-drawer-header">
            {typeof title === 'string' ? (
              <h2 className="rda-drawer-title">{title}</h2>
            ) : (
              <div className="min-w-0 flex-1">{title}</div>
            )}
            {onClose ? (
              <button
                type="button"
                aria-label="Close"
                className="rda-drawer-close"
                onClick={onClose}
              >
                <PiX aria-hidden className="text-lg" />
              </button>
            ) : null}
          </div>
        ) : null}

        <div className={classNames('rda-drawer-body', bodyClass)}>{children}</div>
      </motion.div>
    </Modal>
  )
}

RdaDrawer.displayName = 'RdaDrawer'
